import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { classNames } from 'primereact/utils';
import { CloseIcon, Dialog, InputSearch } from '@repo/atomic-components';
import { useFocusTrap } from '@/hooks';
import { SettingRow } from './SettingRow';
import { filterSettingsGroups } from './filterSettings';
import { useSettingsGroups } from './useSettingsGroups';

type SettingsHubModalProps = {
  /** Group to land on. Falls back to the first group when absent or no longer visible. */
  initialGroupId?: string;
  onHide: () => void;
};

const TITLE_ID = 'settings-hub-title';
const CONTENT_HEADING_ID = 'settings-hub-group-heading';

/** Settings hub modal. Every setting saves on change — there's no Save button. */
export const SettingsHubModal = ({ initialGroupId, onHide }: SettingsHubModalProps) => {
  const groups = useSettingsGroups();
  const [query, setQuery] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState(initialGroupId);

  const setTrapContainer = useFocusTrap<HTMLElement>({ active: true });
  const dialogRef = useRef<HTMLElement | null>(null);

  /**
   * PrimeReact's `aria-hidden` focus sentinels are empty tab stops (WCAG 2.1.1). Taking them
   * out of the tab order hands the job to our own keystroke-based trap.
   */
  const attachFocusTrap = useCallback(
    (node: HTMLDivElement | null) => {
      const dialog = node?.closest<HTMLElement>('[role="dialog"]') ?? null;

      dialog?.querySelectorAll('[data-p-hidden-focusable]').forEach((sentinel) => {
        sentinel.setAttribute('tabindex', '-1');
      });

      dialogRef.current = dialog;
      setTrapContainer(dialog);
    },
    [setTrapContainer],
  );

  /**
   * PrimeReact skips Escape when `closable` is off, so handle it here. A nested overlay can take
   * the first Escape via `defaultPrevented`, and a dialog opened on top of the hub keeps it.
   */
  useEffect(() => {
    const isDialogOnTop = () => {
      const dialogs = document.querySelectorAll('[role="dialog"], [role="alertdialog"]');
      return dialogs.length > 0 && dialogs[dialogs.length - 1] !== dialogRef.current;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented && !isDialogOnTop()) onHide();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onHide]);

  const filteredGroups = useMemo(() => filterSettingsGroups(groups, query), [groups, query]);

  // If the search hides the selected group, show the first matching one. Clearing the search restores the selection.
  const activeGroup = filteredGroups.find((group) => group.id === selectedGroupId) ?? filteredGroups[0];
  const hasResults = filteredGroups.length > 0;
  const matchCount = filteredGroups.reduce((total, group) => total + group.settings.length, 0);

  return (
    <Dialog
      visible
      onHide={onHide}
      modal
      // A click on the mask closes, alongside the header's X and the Escape handler above.
      dismissableMask
      draggable={false}
      closable={false}
      resizable={false}
      // `useFocusTrap` already focuses the modal; PrimeReact's focus-on-show would race with it.
      focusOnShow={false}
      aria-labelledby={TITLE_ID}
      className="!h-[min(40rem,calc(100vh-48px))] !w-[min(55rem,calc(100vw-48px))]"
      pt={{
        root: { className: 'rounded-lg dialog-shadow overflow-hidden' },
        header: { className: 'hidden' },
        content: { className: 'bg-theme-white flex h-full flex-col p-0 rounded-lg' },
      }}
    >
      <div ref={attachFocusTrap} className="flex h-full min-h-0 flex-col">
        <div className="border-theme-border-grey bg-theme-white flex h-14 flex-none items-center justify-between border-b px-4">
          <h1 id={TITLE_ID} className="text-theme-black text-[1.25rem] leading-7.5 font-semibold">
            Settings
          </h1>
          <button
            type="button"
            onClick={onHide}
            aria-label="Close settings"
            className="text-theme-text-grey hover:bg-theme-background-grey hover:text-theme-black focus-visible:outline-fletchers-green-500 flex h-8 w-8 cursor-pointer items-center justify-center rounded"
          >
            <CloseIcon className="h-[12px] w-[12px]" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1">
          <div className="border-theme-border-grey flex w-60 flex-none flex-col border-r">
            <div className="border-theme-border-grey flex-none border-b p-3">
              <InputSearch
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onClear={() => setQuery('')}
                placeholder="Search settings"
                aria-label="Search settings"
                className="!bg-theme-white !text-theme-black !placeholder-theme-text-grey !border-theme-border-grey [&_button:hover]:!text-fletchers-gray-200 !w-full [&_input]:text-sm [&_input]:placeholder:text-sm"
                clearIconClassName="text-theme-black"
              />
            </div>

            <nav aria-label="Settings groups" className="min-h-0 flex-1 overflow-y-auto p-2">
              <ul className="flex flex-col gap-1">
                {filteredGroups.map((group) => {
                  const isActive = group.id === activeGroup?.id;

                  return (
                    <li key={group.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedGroupId(group.id)}
                        aria-current={isActive ? 'true' : undefined}
                        className={classNames(
                          'focus-visible:outline-fletchers-green-500 flex w-full cursor-pointer items-center gap-2 rounded-md p-2 text-left text-sm',
                          isActive
                            ? 'bg-theme-highlight-green text-theme-dark-green font-semibold'
                            : 'text-theme-black hover:bg-theme-border-grey/70',
                        )}
                      >
                        <group.Icon width={16} height={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{group.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          {/* Announces how the list changed as the user types — the filtering is otherwise
              silent for anyone who can't see the nav shrink. */}
          <span aria-live="polite" className="sr-only">
            {query.trim() ? `${matchCount} ${matchCount === 1 ? 'setting matches' : 'settings match'} "${query}"` : ''}
          </span>

          {hasResults && activeGroup ? (
            <section aria-labelledby={CONTENT_HEADING_ID} className="min-w-0 flex-1 overflow-y-auto px-6 py-5">
              <h2 id={CONTENT_HEADING_ID} className="text-theme-black text-base font-semibold">
                {activeGroup.label}
              </h2>
              {activeGroup.description && (
                <p className="text-theme-text-grey mt-1 text-sm">{activeGroup.description}</p>
              )}

              <div className="mt-5 flex flex-col">
                {activeGroup.settings.map((setting, index) => {
                  const labelId = `setting-label-${setting.id}`;

                  return (
                    <div
                      key={setting.id}
                      className={classNames(
                        'py-5',
                        index === 0 && 'pt-0',
                        index < activeGroup.settings.length - 1 && 'border-theme-border-grey border-b',
                      )}
                    >
                      <SettingRow setting={setting} labelId={labelId}>
                        <setting.Control labelId={labelId} />
                      </SettingRow>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : (
            <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-6 text-center">
              <p className="text-theme-black text-sm font-semibold">No settings found</p>
              <p className="text-theme-text-grey text-sm">
                Nothing matches “{query.trim()}”. Try a different word, or clear the search.
              </p>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
